import { drawFitted } from './draw-fitted';
import { FrameContext } from './frame-canvas';

/** The plate comes in a moment after the cut, so the eye first finds the play. */
export const PLATE_DELAY_SEC = 0.5;
/** On screen with the fades; lower-third guides give 3–6 s — long enough to read it twice. */
export const PLATE_VISIBLE_SEC = 5;
export const PLATE_FADE_SEC = 0.3;
/** It slides in from the left by this much while fading in, and back out. */
export const PLATE_SLIDE_PX = 48;
/** Gone before the clip ends, so the fade to black between clips is clean. */
export const PLATE_TAIL_SEC = 0.2;

/** When the plate is on screen within its clip, seconds from the clip start. */
export interface PlateWindow {
  startSec: number;
  durationSec: number;
}

/**
 * The plate's time on screen. A clip shorter than the plate keeps it until just before its end; a clip too short for
 * both fades gets no plate at all (`null`). The same window serves a still template plate and an animated scene.
 */
export function plateWindow(clipDurationSec: number): PlateWindow | null {
  const startSec = PLATE_DELAY_SEC;
  const end = Math.min(startSec + PLATE_VISIBLE_SEC, clipDurationSec - PLATE_TAIL_SEC);
  return end - startSec < 2 * PLATE_FADE_SEC ? null : { startSec, durationSec: end - startSec };
}

export interface PlateAppearance {
  /** 0…1 */
  alpha: number;
  offsetX: number;
}

const easeOutCubic = (x: number) => 1 - (1 - x) ** 3;

/** How a still plate looks `timeSec` after it appeared, in a window of `windowSec`: fade and slide in, then out. */
export function plateFade(timeSec: number, windowSec: number): PlateAppearance {
  const fadeIn = timeSec / PLATE_FADE_SEC;
  const fadeOut = (windowSec - timeSec) / PLATE_FADE_SEC;
  const k = easeOutCubic(Math.min(1, fadeIn, fadeOut));
  return { alpha: k, offsetX: -(1 - k) * PLATE_SLIDE_PX };
}

/** How the still plate looks at `timeSec` from the clip start; `null` — not on screen. */
export function plateAppearance(timeSec: number, clipDurationSec: number): PlateAppearance | null {
  const window = plateWindow(clipDurationSec);
  if (!window || timeSec < window.startSec || timeSec >= window.startSec + window.durationSec) {
    return null;
  }
  return plateFade(timeSec - window.startSec, window.durationSec);
}

export function drawPlate(
  ctx: FrameContext,
  plate: CanvasImageSource,
  appearance: PlateAppearance,
  width: number,
  height: number,
): void {
  ctx.globalAlpha = appearance.alpha;
  drawFitted(ctx, plate, width, height, { offsetX: appearance.offsetX });
  ctx.globalAlpha = 1;
}
